import React, { useCallback, useContext, useEffect, useState } from 'react';
import Map, {
  Layer,
  LayerProps,
  Marker,
  NavigationControl,
  Source,
} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { PGEServiceTerritory } from '../pge-service-territory/pge-service-territory';
import { OutagesClusterOverlay } from '../outages-cluster-overlay';
import { AccountMarkerOverlay } from '../account-marker-overlay';
import { useGetAllOutageEvents } from '../../hooks/useGetAllOutageEvents';
import Backdrop from '../../../backdrop/Backdrop';
import { useTranslation } from '../../../../hooks/useTranslation';
import { CommunityResourceCenter } from '../community-resource-center';
import {
  OutageMarkerView,
  useOutageLeftPanelState,
} from '../../providers/OutageLeftPanelStateProvider';
import CurrentLocationIcon from '../icons/CurrentLocation';
import { Position, PSPSZoneAttributes } from '../../types';
import {
  ShowByType,
  useOutageMapOptions,
} from '../../providers/OutageMapOptionsProvider';
import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { useOutageAccountListContext } from '../../providers/OutageAccountListProvider';
import { useGetZipBoundaries } from '../../hooks/useGetZipBoundries';
import OutageMarkerIcon from '../icons/OutageMarkerIcon';
import mainColors from '../../../../themes/main-colors';
import { useGetOutagesByCountyOrZip } from '../../hooks/useGetOutagesByCountyOrZip';
import { useGetCountyBoundaries } from '../../hooks/useCountyBoundries';
import { useGetOutagesByCounty } from '../../hooks/useGetOutagesByCounty';
import { PSPSOverlayLayer } from '../psps-overlay-layer';
import { useOutageMapContext } from '../../providers/OutageMapProvider';
import { CONSTANTS } from '../../constants';
import { featureFlagsContext } from '../../../../providers/FeatureFlagsProvider';
import { ReportOutageByCountyCity } from '../report-outage-by-county-city';
import { BoundaryLayer } from '../boundary-layer';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';
import { useGetAllPlannedOutageEvents } from '../../hooks/useGetAllPlannedOutageEvents';
import { PlannedOutagesOverlay } from '../planned-outages-overlay';

interface OutageMapSectionProps {
  currentUserLocation: Position | undefined;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mapContainer: {
      width: '100%',
      height: '100%',
      '& .mapboxgl-canvas': {
        width: '100% !important',
      },
    },
    icon: {
      width: '32px',
      height: '32px',
      cursor: 'pointer',
      transition: '0.2s',
      '&:hover': {
        width: '40px',
        height: '40px',
        zIndex: 4,
      },
    },
  }),
);

export const OutageMapSection = ({
  currentUserLocation,
}: OutageMapSectionProps) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { outages, loading } = useGetAllOutageEvents();
  const {
    plannedOutageData,
    loading: plannedOutagesLoading,
  } = useGetAllPlannedOutageEvents();
  const { accountListWithOutageInfo } = useOutageAccountListContext();
  const {
    selectedAccountForOutage,
    setOutageMarkerView,
    setPspsDetails,
    outageMarkerView,
  } = useOutageLeftPanelState();
  const [hoveredPSPSFeatureId, setHoveredPSPSFeatureId] = useState(null);
  const [openCountyCityPanel, setOpenCountyCityPanel] = useState(false);
  const [defaultTabForPanel, setDefaultTabForPanel] = useState(0);
  const { setPage } = useOutageResponsivePageTracker();

  const {
    mapOptions,
    isLoaded,
    mapInstance,
    fitBounds,
  } = useOutageMapContext();
  const {
    outageToggles,
    showBy,
    setCountyOutagesCenters,
    setZipOutagesCenters,
  } = useOutageMapOptions();
  const {
    outages: outagesByCountyOrZip,
    loading: outagesByCountyOrZipLoading,
  } = useGetOutagesByCountyOrZip();
  const {
    outages: outagesByCounty,
    loading: outagesByCountyLoading,
  } = useGetOutagesByCounty();

  const { zipCenters, geoJson: zipBoundaryGeoJson } = useGetZipBoundaries(
    mapInstance,
    outagesByCountyOrZip,
  );

  const {
    countyCenters,
    geoJson: countyBoundaryGeoJson,
  } = useGetCountyBoundaries(mapInstance, outagesByCounty);

  const { isOutagesPSPSEnabled, loading: featureFlagLoading } = useContext(
    featureFlagsContext,
  );

  useEffect(() => {
    if (!selectedAccountForOutage) {
      return;
    }

    const servicePoint = selectedAccountForOutage.premiseInfos?.[0];
    if (!servicePoint?.servicePointDetails?.length) {
      return;
    }

    const outageServicePoint = servicePoint.servicePointDetails.find(
      sp => sp?.outageInfo?.hasKnownOutage,
    );

    const point = outageServicePoint ?? servicePoint.servicePointDetails[0];
    if (!point) {
      return;
    }

    mapInstance?.flyTo({
      center: [point.long, point.lat],
      zoom: 15,
      duration: 0,
      essential: true,
    });
  }, [selectedAccountForOutage, mapInstance]);

  useEffect(() => {
    if (!outageMarkerView && selectedAccountForOutage) {
      const servicePoint = selectedAccountForOutage.premiseInfos?.[0];
      if (!servicePoint?.servicePointDetails?.length) {
        return;
      }

      const outageServicePoint = servicePoint.servicePointDetails.find(
        sp => sp?.outageInfo?.hasKnownOutage,
      );

      const point = outageServicePoint ?? servicePoint.servicePointDetails[0];
      if (!point) {
        return;
      }

      mapInstance?.flyTo({
        center: [point.long, point.lat],
        zoom: 15,
        duration: 0,
        essential: true,
      });
    }
  }, [outageMarkerView]);

  useEffect(() => {
    if (currentUserLocation) {
      mapInstance?.flyTo({
        center: [currentUserLocation.lng, currentUserLocation.lat],
        zoom: 15,
        duration: 0,
        essential: true,
      });
    }
  }, [currentUserLocation]);

  useEffect(() => {
    if (countyCenters && countyCenters.length) {
      setCountyOutagesCenters(countyCenters);
    }
  }, [countyCenters]);

  useEffect(() => {
    if (zipCenters && zipCenters.length) {
      setZipOutagesCenters(zipCenters);
    }
  }, [zipCenters]);

  const handleMouseMove = useCallback(event => {
    if (!isOutagesPSPSEnabled) {
      return;
    }

    //Highlight PSPS zone on hover on it
    const features = event?.target?.queryRenderedFeatures(event.point, {
      layers: [CONSTANTS.MAP_CONFIG.pspsLayerId],
    });

    const feature = features?.[0];

    if (feature) {
      setHoveredPSPSFeatureId(feature?.id);
      event.target.getCanvas().style.cursor = 'pointer'; // Change cursor
    } else {
      event.target.getCanvas().style.cursor = 'default'; // Change cursor
      setHoveredPSPSFeatureId(null);
    }
  }, []);

  const handleClick = useCallback(event => {
    if (!isOutagesPSPSEnabled) {
      return;
    }
    //Waiting to get defaultPrevented updated from the marker clicks
    setTimeout(() => {
      //Stop executing when the event is originated from marker click
      if (event.originalEvent?.defaultPrevented) {
        return;
      }
      //Display selected PSPS zone details
      const features = event?.target?.queryRenderedFeatures(event.point, {
        layers: [CONSTANTS.MAP_CONFIG.pspsLayerId],
      });
      const feature = features?.[0];

      if (feature) {
        const attributes = feature?.properties as PSPSZoneAttributes;
        //For mobile responsive, invoke the details panel
        setPage(ResponsivePageView.Details);

        setOutageMarkerView(OutageMarkerView.PSPS);
        setPspsDetails({ zone: attributes?.Name, status: attributes?.Status });
      }
    }, 100);
  }, []);

  let pspsZoneHandlers = {};

  //Attach the handler only when PSPS is enabled to track the clicks on zones
  if (isOutagesPSPSEnabled) {
    pspsZoneHandlers = {
      onClick: handleClick,
      onMouseMove: handleMouseMove,
    };
  }

  return (
    <>
      {(loading || outagesByCountyOrZipLoading || outagesByCountyLoading) && ( //||
        //  plannedOutagesLoading TODO: very slow api; this loader is under discussion
        <Backdrop forceOpen message={t('LOADING_OUTAGE_INFO_SPINNER')} />
      )}
      <Box className={classes.mapContainer}>
        <Map {...mapOptions} projection={undefined} {...pspsZoneHandlers}>
          <PGEServiceTerritory />

          {showBy === ShowByType.DEFAULT && (
            <>
              {isLoaded &&
                outages?.length > 0 &&
                mapInstance &&
                outageToggles.unplanned && (
                  <OutagesClusterOverlay outages={outages} />
                )}

              {plannedOutageData && outageToggles.planned && (
                <PlannedOutagesOverlay plannedOutages={plannedOutageData} />
              )}

              {accountListWithOutageInfo &&
                accountListWithOutageInfo?.length > 0 &&
                isLoaded &&
                mapInstance && (
                  <AccountMarkerOverlay
                    accountDetails={accountListWithOutageInfo}
                    selectedAccount={selectedAccountForOutage}
                  />
                )}

              {isLoaded &&
                mapInstance &&
                !featureFlagLoading &&
                isOutagesPSPSEnabled &&
                outageToggles.psps && <CommunityResourceCenter />}

              {currentUserLocation && (
                <Marker
                  latitude={currentUserLocation?.lat}
                  longitude={currentUserLocation?.lng}
                  style={{ zIndex: 5 }}
                >
                  <CurrentLocationIcon width={50} height={50} />
                </Marker>
              )}
            </>
          )}

          {!featureFlagLoading && isOutagesPSPSEnabled && (
            <PSPSOverlayLayer hoveredPSPSFeatureId={hoveredPSPSFeatureId} />
          )}

          {showBy === ShowByType.ZIP &&
            !outagesByCountyOrZipLoading &&
            zipBoundaryGeoJson && (
              <>
                <BoundaryLayer
                  sourceId="zip-boundaries"
                  geoJson={zipBoundaryGeoJson}
                />
                {zipCenters.map(c => (
                  <Marker
                    key={c.zipCode}
                    longitude={c.center[0]}
                    latitude={c.center[1]}
                    onClick={() => {
                      setDefaultTabForPanel(1);
                      setOpenCountyCityPanel(true);
                    }}
                  >
                    <OutageMarkerIcon
                      text={c.count}
                      variant={'square'}
                      className={classes.icon}
                      size={40}
                      fillColor={mainColors.white}
                      strokeColor={mainColors.roseRed}
                      textColor={mainColors.roseRed}
                    />
                  </Marker>
                ))}
              </>
            )}
          {showBy === ShowByType.COUNTY &&
            !outagesByCountyLoading &&
            countyBoundaryGeoJson && (
              <>
                <BoundaryLayer
                  sourceId="county-boundaries"
                  geoJson={countyBoundaryGeoJson}
                />
                {countyCenters.map(c => (
                  <Marker
                    key={c.county}
                    longitude={c.center[0]}
                    latitude={c.center[1]}
                    onClick={() => {
                      setDefaultTabForPanel(0);
                      setOpenCountyCityPanel(true);
                    }}
                  >
                    <OutageMarkerIcon
                      text={c.count}
                      variant={'square'}
                      className={classes.icon}
                      size={40}
                      fillColor={mainColors.white}
                      strokeColor={mainColors.roseRed}
                      textColor={mainColors.roseRed}
                    />
                  </Marker>
                ))}
              </>
            )}

          <NavigationControl
            showZoom
            showCompass={false}
            position="bottom-right"
          />
        </Map>
      </Box>
      <ReportOutageByCountyCity
        open={openCountyCityPanel}
        setOpen={setOpenCountyCityPanel}
        defaultTab={defaultTabForPanel}
      />
    </>
  );
};
